import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { doc, getDoc, collection, query, where, onSnapshot, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import {
    ShieldCheck,
    ArrowLeft,
    ChevronRight,
    FileText,
    Lock,
    Eye
} from "lucide-react";
import ATTDecisionPanel from "./ATTDecisionPanel";

const SECTIONS = [
    { id: "tradeCompliance", title: "Trade Compliance" },
    { id: "supplyChain", title: "Supply Chain Security" },
    { id: "financial", title: "Financial Viability" },
    { id: "personnel", title: "Personnel Security" }
];

export default function ATTApplicationReview() {
    const { applicationId } = useParams();
    const navigate = useNavigate();
    const [application, setApplication] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!applicationId) return;

        // Fetch application
        const fetchApp = async () => {
            const docRef = doc(db, "attApplications", applicationId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setApplication({ id: docSnap.id, ...docSnap.data() });
            }
        };

        // Fetch documents
        const q = query(collection(db, "attDocuments"), where("applicationId", "==", applicationId));
        const unsubscribeDocs = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setDocuments(docs);
            setLoading(false);
        });

        fetchApp();
        return () => unsubscribeDocs();
    }, [applicationId]);

    const handleDecision = async (decision) => {
        if (!selectedDoc || !application) return;

        try {
            // 1. Update Document status in Firestore
            const docRef = doc(db, "attDocuments", selectedDoc.id);
            await updateDoc(docRef, {
                status: decision.status,
                agentNotes: decision.notes,
                reviewedAt: serverTimestamp()
            });

            // 2. Add message to client inbox
            await addDoc(collection(db, "attMessages"), {
                applicationId,
                sender: "agent",
                documentId: selectedDoc.id,
                message: decision.notes,
                timestamp: serverTimestamp()
            });

            // 3. Update application state object
            const updatedSteps = { ...(application.steps || {}) };
            const stepId = selectedDoc.step;
            const taskId = selectedDoc.taskId;

            if (updatedSteps[stepId]) {
                if (!updatedSteps[stepId].tasks) updatedSteps[stepId].tasks = {};
                updatedSteps[stepId].tasks[taskId] = {
                    ...updatedSteps[stepId].tasks[taskId],
                    doc: selectedDoc.name,
                    uploaded: true,
                    status: decision.status, // Store lowercase: approved, rejected, needs_fix
                    note: decision.notes
                };

                // Update global application status if all docs are approved
                let allApproved = true;
                Object.values(updatedSteps).forEach(step => {
                    Object.values(step.tasks || {}).forEach(task => {
                        if (task.status?.toLowerCase() !== "approved") allApproved = false;
                    });
                });

                const appStatus = allApproved ? "ready_for_abf" : "under_review";

                await updateDoc(doc(db, "attApplications", applicationId), {
                    steps: updatedSteps,
                    status: appStatus
                });
            }

            setSelectedDoc(null);
            alert("Decision submitted successfully.");
        } catch (err) {
            console.error("Audit action failed:", err);
            alert("Error submitting decision. Please check connection.");
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-32 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-navy-900 animate-bounce flex items-center justify-center">
                    <ShieldCheck className="text-gold-500" />
                </div>
                <p className="text-xs font-bold text-navy-900 uppercase tracking-widest animate-pulse">Initialising Audit Environment...</p>
            </div>
        );
    }

    return (
        <div className="p-8 space-y-8 animate-in fade-in duration-700 max-w-[1600px] mx-auto">
            {/* Nav */}
            <button
                onClick={() => navigate("/agent")}
                className="flex items-center gap-2 text-[10px] font-bold text-slate-400 hover:text-navy-900 transition-all group uppercase tracking-widest"
            >
                <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to Agent Portfolio
            </button>

            {/* Header */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                    <ShieldCheck size={120} />
                </div>

                <div className="flex items-center gap-6 relative z-10">
                    <div className="w-16 h-16 rounded-2xl bg-navy-900 flex items-center justify-center text-gold-500 shadow-xl shadow-navy-900/10">
                        <ShieldCheck size={32} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-navy-900 font-serif lowercase"><span className="uppercase">{application?.clientName?.slice(0, 1)}</span>{application?.clientName?.slice(1)}</h1>
                        <div className="flex items-center gap-4 mt-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">SUBMISSION ID: {applicationId.slice(0, 8)}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border capitalize ${application?.status?.toLowerCase() === "ready_for_abf" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-blue-100 text-blue-700 border-blue-200"
                                }`}>
                                {application?.status?.replace('_', ' ') || "Pending Review"}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 relative z-10">
                    <div className="bg-slate-50 px-6 py-3 rounded-xl border border-slate-200 text-right">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Compliance Score</p>
                        <p className="text-2xl font-bold text-navy-900">{application?.progress || 0}%</p>
                    </div>
                </div>
            </div>

            {/* Main Layout Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
                {/* Sidebar: Navigation Sections */}
                <div className="xl:col-span-3 space-y-4">
                    {SECTIONS.map((section) => {
                        const sectionDocs = documents.filter(d => d.step === section.id);
                        return (
                            <div key={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                    <h3 className="font-bold text-navy-900 text-[10px] uppercase tracking-wider">{section.title}</h3>
                                    <span className="text-[10px] text-slate-400 font-bold">{sectionDocs.length} Docs</span>
                                </div>
                                <div className="divide-y divide-slate-50">
                                    {sectionDocs.map((doc) => (
                                        <button
                                            key={doc.id}
                                            onClick={() => setSelectedDoc(doc)}
                                            className={`w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-all text-left group ${selectedDoc?.id === doc.id ? "bg-navy-50/50 border-l-4 border-l-navy-900" : ""
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm border ${doc.status?.toLowerCase() === "approved" ? "bg-emerald-50 text-emerald-600 border-emerald-100" :
                                                    doc.status?.toLowerCase() === "needs_fix" || doc.status?.toLowerCase() === "needs fix" ? "bg-orange-50 text-orange-600 border-orange-100" :
                                                        doc.status?.toLowerCase() === "pending_review" ? "bg-blue-50 text-blue-600 border-blue-100" : "bg-white text-slate-400 border-slate-100"
                                                    }`}>
                                                    <FileText size={16} />
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-bold text-navy-900 leading-tight">{doc.name}</p>
                                                    <p className={`text-[9px] font-bold mt-0.5 uppercase tracking-tighter ${doc.status?.toLowerCase() === "approved" ? "text-emerald-500" :
                                                        doc.status?.toLowerCase() === "needs_fix" || doc.status?.toLowerCase() === "needs fix" ? "text-orange-500" :
                                                            doc.status?.toLowerCase() === "pending_review" ? "text-blue-500" : "text-slate-400"
                                                        }`}>{doc.status?.replace('_', ' ') || "Review Required"}</p>
                                                </div>
                                            </div>
                                            <ChevronRight size={14} className="text-slate-300 group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    ))}
                                    {sectionDocs.length === 0 && (
                                        <div className="p-8 text-center bg-slate-50/50">
                                            <Lock size={20} className="mx-auto text-slate-300 mb-2 opacity-50" />
                                            <p className="text-[10px] text-slate-400 italic">Evidence Lock: No submissions.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Audit Workspace */}
                <div className="xl:col-span-9 space-y-6 lg:sticky lg:top-8">
                    {selectedDoc ? (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[750px] animate-in slide-in-from-right-4 duration-500">
                            {/* Evidence Preview */}
                            <div className="lg:col-span-8 bg-white rounded-2xl overflow-hidden border border-slate-200 relative group flex flex-col shadow-xl">
                                <div className="p-4 bg-navy-900 flex justify-between items-center text-[10px] font-bold text-white">
                                    <span className="flex items-center gap-2">
                                        <Eye size={14} className="text-gold-500" /> AUDIT PREVIEW: {selectedDoc.name}
                                    </span>
                                    <button
                                        className="text-white/50 hover:text-white transition-all underline"
                                        onClick={() => window.open(selectedDoc.url, '_blank')}
                                    >
                                        Open External Tab
                                    </button>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <iframe
                                        src={selectedDoc.url}
                                        title="Audit Preview"
                                        className="w-full h-full border-none"
                                    />
                                </div>
                                <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-bold px-6">
                                    <span>Task ID: {selectedDoc.taskId}</span>
                                    <span>Uploaded: {selectedDoc.createdAt?.seconds ? new Date(selectedDoc.createdAt.seconds * 1000).toLocaleString() : 'N/A'}</span>
                                </div>
                            </div>

                            {/* Actions & Feedback */}
                            <div className="lg:col-span-4 h-full">
                                <ATTDecisionPanel
                                    initialNotes={selectedDoc.agentNotes || ""}
                                    currentStatus={selectedDoc.status || "pending"}
                                    onSubmit={handleDecision}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-dashed border-slate-300 rounded-[2.5rem] h-[600px] flex flex-col items-center justify-center p-12 text-center group shadow-sm">
                            <div className="w-24 h-24 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 mb-6 group-hover:scale-110 transition-transform duration-500 border border-slate-100">
                                <ShieldCheck size={48} />
                            </div>
                            <h3 className="text-xl font-bold text-navy-900 font-serif">Audit Workspace Ready</h3>
                            <p className="text-sm text-slate-400 max-w-sm mt-3 leading-relaxed">Please select a compliance marker from the left panel to begin the verification process for {application?.clientName}.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
